import { z } from "zod";
import { FinancingHistoryField } from "@remi/enums/financing/history/field";
import { FinancingStatusSchema } from "@remi/enums/financing-status";
import { FinancingVendorSchema } from "@remi/enums/financing-vendor";
import { FundingPaymentMethodSchema } from "@remi/enums/funding-payment-method";
import type { EqualWithDiff, Expect } from "@remi/ts-utils";

import { HistoryStringBooleanSchema } from "../../history";

const variantByField = {
  status: z.object({
    field: z.literal(FinancingHistoryField.status),
    changedTo: FinancingStatusSchema,
    changedFrom: FinancingStatusSchema.nullable(),
  }),
  loanAmount: z.object({
    field: z.literal(FinancingHistoryField.loanAmount),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number(),
  }),
  maxQualifiedAmount: z.object({
    field: z.literal(FinancingHistoryField.maxQualifiedAmount),
    changedTo: z.coerce.number().nullable(),
    changedFrom: z.coerce.number().nullable(),
  }),
  disbursedAmount: z.object({
    field: z.literal(FinancingHistoryField.disbursedAmount),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number(),
  }),
  fullyDisbursedDate: z.object({
    field: z.literal(FinancingHistoryField.fullyDisbursedDate),
    changedTo: z.coerce.date().nullable(),
    changedFrom: z.coerce.date().nullable(),
  }),
  finalizedDate: z.object({
    field: z.literal(FinancingHistoryField.finalizedDate),
    changedTo: z.coerce.date().nullable(),
    changedFrom: z.coerce.date().nullable(),
  }),
  apr: z.object({
    field: z.literal(FinancingHistoryField.apr),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number(),
  }),
  termMonths: z.object({
    field: z.literal(FinancingHistoryField.termMonths),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number(),
  }),
  cancellationFee: z.object({
    field: z.literal(FinancingHistoryField.cancellationFee),
    changedTo: z.coerce.number().nullable(),
    changedFrom: z.coerce.number().nullable(),
  }),
  isPlaceholder: z.object({
    field: z.literal(FinancingHistoryField.isPlaceholder),
    changedTo: HistoryStringBooleanSchema,
    changedFrom: HistoryStringBooleanSchema,
  }),
  paymentMethod: z.object({
    field: z.literal(FinancingHistoryField.paymentMethod),
    changedTo: FundingPaymentMethodSchema.nullable(),
    changedFrom: FundingPaymentMethodSchema.nullable(),
  }),
  externalId: z.object({
    field: z.literal(FinancingHistoryField.externalId),
    changedTo: z.string(),
    changedFrom: z.string().nullable(),
  }),
  requestForQuoteId: z.object({
    field: z.literal(FinancingHistoryField.requestForQuoteId),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number().nullable(),
  }),
  vendor: z.object({
    field: z.literal(FinancingHistoryField.vendor),
    changedTo: FinancingVendorSchema,
    changedFrom: FinancingVendorSchema.nullable(),
  }),
  loanDocumentsSignedAt: z.object({
    field: z.literal(FinancingHistoryField.loanDocumentsSignedAt),
    changedTo: z.coerce.date().nullable(),
    changedFrom: z.coerce.date().nullable(),
  }),
  monthlyPayment: z.object({
    field: z.literal(FinancingHistoryField.monthlyPayment),
    changedTo: z.coerce.number().nullable(),
    changedFrom: z.coerce.number().nullable(),
  }),
  stipulations: z.object({
    field: z.literal(FinancingHistoryField.stipulations),
    changedTo: z.string().nullable(),
    changedFrom: z.string().nullable(),
  }),
  stipulationsSubmittedAt: z.object({
    field: z.literal(FinancingHistoryField.stipulationsSubmittedAt),
    changedTo: z.coerce.date().nullable(),
    changedFrom: z.coerce.date().nullable(),
  }),
  submissionError: z.object({
    field: z.literal(FinancingHistoryField.submissionError),
    changedTo: z.string().nullable(),
    changedFrom: z.string().nullable(),
  }),
  loanDocumentsSigningUrl: z.object({
    field: z.literal(FinancingHistoryField.loanDocumentsSigningUrl),
    changedTo: z.string().nullable(),
    changedFrom: z.string().nullable(),
  }),
  creditExpirationDate: z.object({
    field: z.literal(FinancingHistoryField.creditExpirationDate),
    changedTo: z.coerce.date().nullable(),
    changedFrom: z.coerce.date().nullable(),
  }),
  loanPlanId: z.object({
    field: z.literal(FinancingHistoryField.loanPlanId),
    changedTo: z.string().nullable(),
    changedFrom: z.string().nullable(),
  }),
  deletedAt: z.object({
    field: z.literal(FinancingHistoryField.deletedAt),
    changedTo: z.coerce.date().nullable(),
    changedFrom: z.coerce.date().nullable(),
  }),
} as const satisfies Record<FinancingHistoryField, z.ZodType>;

export const FinancingHistorySchema = z
  .object({
    id: z.string(),
    financingId: z.string(),
    changedById: z.string(),
    createdAt: z.coerce.date(),
  })
  .and(
    z.discriminatedUnion("field", [
      variantByField.status,
      variantByField.loanAmount,
      variantByField.maxQualifiedAmount,
      variantByField.disbursedAmount,
      variantByField.fullyDisbursedDate,
      variantByField.finalizedDate,
      variantByField.apr,
      variantByField.termMonths,
      variantByField.cancellationFee,
      variantByField.isPlaceholder,
      variantByField.paymentMethod,
      variantByField.externalId,
      variantByField.requestForQuoteId,
      variantByField.vendor,
      variantByField.loanDocumentsSignedAt,
      variantByField.monthlyPayment,
      variantByField.stipulations,
      variantByField.stipulationsSubmittedAt,
      variantByField.submissionError,
      variantByField.loanDocumentsSigningUrl,
      variantByField.creditExpirationDate,
      variantByField.loanPlanId,
      variantByField.deletedAt,
    ]),
  );

type _EveryFieldIsReadable = Expect<
  EqualWithDiff<z.infer<typeof FinancingHistorySchema>["field"], FinancingHistoryField>
>;

export type FinancingHistory = z.infer<typeof FinancingHistorySchema>;
